// src/components/auth/AuthCallbackPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import logger from '../../utils/logger';

/**
 * Authorization Callback Page
 * Handles redirect from Microsoft OAuth flow
 */
const AuthCallbackPage = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authorization response...');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth(); // Use auth context to access authentication state

  useEffect(() => {
    const processAuthCallback = async () => {
      try {
        logger.log('=========== AUTH CALLBACK PROCESSING STARTED ===========');
        logger.log(`Auth callback processing started at: ${new Date().toISOString()}`);
        logger.log(`Current URL: ${window.location.href.split('?')[0]}?[params hidden]`);

        // Get query parameters from URL
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const status = searchParams.get('status');
        
        logger.log('Auth callback parameters:');
        logger.log(`- Has authorization code: ${!!code ? 'YES' : 'NO'}`);
        logger.log(`- Has error: ${!!error ? 'YES' : 'NO'}`);
        logger.log(`- Status parameter: ${status || 'Not provided'}`);
        
        // Try to extract state parameter which may contain registrationId
        const stateParam = searchParams.get('state');
        let registrationId = localStorage.getItem('registrationId');
        
        logger.log('Registration ID lookup:');
        logger.log(`- Initial registrationId from localStorage: ${registrationId || 'Not found'}`);
        logger.log(`- State parameter present: ${!!stateParam ? 'YES' : 'NO'}`);
        
        // Try to parse registrationId from state parameter
        if (stateParam) {
          try {
            logger.log(`- Attempting to parse state parameter: ${stateParam.substring(0, 20)}...`);
            const stateObj = JSON.parse(stateParam);
            if (stateObj.registrationId) {
              registrationId = stateObj.registrationId;
              logger.log(`- Successfully extracted registration ID from state: ${registrationId}`);
              localStorage.setItem('registrationId', registrationId);
              logger.log('- Stored registration ID in localStorage');
            } else {
              logger.log('- State parameter does not contain registrationId');
            }
          } catch (e) {
            logger.warn('- Failed to parse state parameter:', e.message);
            logger.log(`- Raw state parameter value: ${stateParam}`);
          }
        } else {
          logger.log('- No state parameter found in callback URL');
        }
        
        // If we still don't have a registrationId, check if there's one in the URL directly
        if (!registrationId) {
          const urlRegistrationId = searchParams.get('registration_id');
          logger.log(`- Checking for registration_id in URL parameters: ${urlRegistrationId || 'Not found'}`);
          
          if (urlRegistrationId) {
            registrationId = urlRegistrationId;
            logger.log(`- Found registration ID in URL parameter: ${registrationId}`);
            localStorage.setItem('registrationId', registrationId);
            logger.log('- Stored registration ID from URL in localStorage');
          } else {
            logger.log('- No registration_id found in URL parameters');
          }
        }
        
        logger.log(`Final registration ID for PKCE verification: ${registrationId || 'NONE - THIS WILL CAUSE PKCE VERIFICATION TO FAIL'}`);
        
        // Log presence of authorization code
        if (code) {
          logger.log(`Authorization code received: ${code.substring(0, 10)}...`);
          logger.log(`Code length: ${code.length} characters`);
        } else {
          logger.log('No authorization code present in callback URL - this is a problem if not an error case');
        }

        // Handle status success from backend redirect
        if (status === 'success') {
          setStatus('success');
          setMessage('Authorization successful! Redirecting...');
          
          // The backend has already processed the code and stored tokens
          logger.log('Authorization successful according to status parameter');
          logger.log(`Token received: ${searchParams.get('tokenReceived') || 'Not specified'}`);
          logger.log(`Token stored in BC: ${searchParams.get('tokenStoredInBC') || 'Not specified'}`);
          
          // Notify the opener window if this is a popup
          if (window.opener) {
            logger.log('Sending success message to opener window');
            window.opener.postMessage({
              type: 'AUTH_SUCCESS',
              registrationId: searchParams.get('registrationId'),
              timestamp: Date.now(),
              tokenReceived: searchParams.get('tokenReceived') === 'true',
              tokenStoredInBC: searchParams.get('tokenStoredInBC') === 'true'
            }, '*');
            
            // Close this popup after a short delay
            setTimeout(() => {
              window.close();
            }, 2000);
          } else {
            // If not a popup, redirect to admin authorization page
            setTimeout(() => {
              navigate('/admin/authorization', { replace: true });
            }, 2000);
          }
          return;
        }
        
        // Handle error case
        if (error) {
          logger.error('Authorization error:', error, errorDescription);
          setStatus('error');
          setMessage(`Authorization failed: ${errorDescription || error}`);
          return;
        }

        // Handle missing code
        if (!code) {
          logger.error('No authorization code provided');
          setStatus('error');
          setMessage('No authorization code was provided in the callback.');
          return;
        }

        // CRITICAL ERROR: This code is no longer needed!
        // In our new flow, Azure should redirect directly to the backend
        // If you're seeing this code execute, it means Azure is redirecting to the frontend
        // instead of the backend, which is not what we want
        
        logger.error('=========== AUTHENTICATION FLOW ERROR ===========');
        logger.error('We received an authorization code directly in the frontend!');
        logger.error('This indicates Azure AD is redirecting to the wrong URL.');
        logger.error('The authorization code should be sent directly to the backend.');
        logger.error('');
        logger.error('URGENT: Check your Azure AD app registration and ensure');
        logger.error('the Redirect URI is set to the BACKEND, not the frontend:');
        logger.error('https://demo-dev-backend-demo.uksouth-01.azurewebsites.net/auth/callback');
        logger.error('');
        logger.error('Attempting to redirect to admin page with error...');
        
        setStatus('error');
        setMessage('Authentication flow configuration error. The authorization code was sent to the frontend instead of the backend. Please check your Azure AD app registration.');
        
        // Notify the opener window of the error
        if (window.opener) {
          window.opener.postMessage({
            type: 'AUTH_ERROR',
            error: 'Authentication flow configuration error',
            message: 'The authorization code was sent to the frontend instead of the backend',
            timestamp: Date.now()
          }, '*');
          
          setTimeout(() => {
            window.close();
          }, 5000);
        } else {
          setTimeout(() => {
            navigate('/admin/authorization', { 
              replace: true,
              state: { 
                error: 'Authentication flow configuration error',
                message: 'The authorization code was sent to the frontend instead of the backend'
              }
            });
          }, 5000);
        }
      } catch (error) {
        logger.error('=========== FATAL ERROR IN AUTH CALLBACK ===========');
        logger.error('Error processing authorization callback:', error.message);
        logger.error('Error stack:', error.stack);
        logger.error(`Error occurred at: ${new Date().toISOString()}`);
        
        // Log all available parameters to help debug the issue
        try {
          const searchParams = new URLSearchParams(location.search);
          logger.error('Available URL parameters:', Object.fromEntries(searchParams.entries()));
          logger.error('LocalStorage registration ID:', localStorage.getItem('registrationId'));
          logger.error('Browser info:', navigator.userAgent);
          logger.error('Window opener exists:', !!window.opener);
        } catch (logError) {
          logger.error('Error while logging debug info:', logError);
        }
        
        setStatus('error');
        setMessage(`Error processing authorization: ${error.message}`);
      }
    };

    processAuthCallback();
  }, [location, navigate]);

  // Render based on status
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white shadow rounded-lg p-8">
        <div className="flex flex-col items-center">
          {status === 'processing' && (
            <div className="flex flex-col items-center">
              <RefreshCw className="h-12 w-12 text-indigo-500 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Authorization</h2>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex flex-col items-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authorization Successful</h2>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex flex-col items-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authorization Failed</h2>
            </div>
          )}
          
          <p className="text-gray-600 text-center mt-2">{message}</p>
          
          {status === 'error' && (
            <button
              onClick={() => navigate('/admin/authorization')}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Return to Authorization Page
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallbackPage;